import { useEffect, useMemo, useState } from 'react'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface DeliveryFeePopoverProps {
  /** Fee currently in effect (override when set, else the settings default). */
  current: number
  /** True when a manual override is active (vs. the settings default). */
  overridden: boolean
  /** Settings default fee — restored by "Reset". */
  defaultFee: number
  /** Apply a manual override (>= 0; 0 = free delivery). */
  onApply: (next: number) => void
  /** Clear the override, reverting to the settings default. */
  onReset: () => void
  /** Gates Apply/Reset while a mutation is in flight. */
  pending?: boolean
  disabled?: boolean
}

const fmt = (n: number) =>
  n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * Inline editor for the per-order delivery fee. Mirrors LineDiscountPopover:
 * a small pencil trigger opens a popover with a single amount field, Apply,
 * and (when an override is active) Reset-to-default. The edited value replaces
 * the settings default for this one order and flows to the receipt + total.
 */
export function DeliveryFeePopover({
  current,
  overridden,
  defaultFee,
  onApply,
  onReset,
  pending,
  disabled,
}: DeliveryFeePopoverProps) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState<string>(String(current ?? 0))

  // Re-seed the field from the live value each time the popover opens so a
  // stale edit can't overwrite a fee changed via another path.
  useEffect(() => {
    if (open) setValue(String(current ?? 0))
  }, [open, current])

  const parsed = useMemo(() => {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : NaN
  }, [value])

  const error = useMemo<string | null>(() => {
    if (value.trim() === '') return 'Enter an amount'
    if (!Number.isFinite(parsed) || parsed < 0) return 'Enter a positive number (0 = free)'
    return null
  }, [parsed, value])

  const canApply = !pending && !error && Number.isFinite(parsed) && parsed >= 0

  const handleApply = () => {
    if (!canApply) return
    onApply(parsed)
    setOpen(false)
  }

  const handleReset = () => {
    onReset()
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={overridden ? `Edit delivery fee (custom Rs ${fmt(current)})` : 'Edit delivery fee'}
          aria-label="Edit delivery fee"
          className={cn(
            'inline-flex h-5 w-5 items-center justify-center rounded border transition-colors',
            overridden
              ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200'
              : 'border-border bg-background text-muted-foreground hover:bg-muted',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <Pencil className="h-3 w-3" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60" align="end">
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Delivery fee
            </Label>
            <span className="text-[11px] text-muted-foreground">Default Rs {fmt(defaultFee)}</span>
          </div>
          <Input
            inputMode="decimal"
            autoFocus
            className="h-9 text-sm"
            placeholder="0.00"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canApply) {
                e.preventDefault()
                handleApply()
              }
            }}
          />
          {error ? (
            <p className="text-[11px] text-destructive">{error}</p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Replaces the default on this order. Flows to the receipt &amp; total.
            </p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              className="h-8 flex-1"
              disabled={!canApply}
              onClick={handleApply}
            >
              Apply
            </Button>
            {overridden && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-xs"
                disabled={pending}
                onClick={handleReset}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
